import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container, Paper, Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Grid, Autocomplete, CircularProgress, FormControlLabel, Switch, Alert, Snackbar } from '@mui/material';
import { AccountBalance as BankIcon } from '@mui/icons-material';
import { Add as AddIcon } from '@mui/icons-material';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { explainAsClient, explainAsEntity, createBankLine } from '../utils/bankService';
import { useAuth } from '../contexts/AuthContext';
import NumericField from './NumericField';

export default function BankManagement() {
  const { userProfile, currentUser, isSuperAdmin } = useAuth();
  const [lines, setLines] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const tableRef = useRef(null);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(lines.length / rowsPerPage) - 1));
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [clients, setClients] = useState([]);
  const [openExplain, setOpenExplain] = useState(false);
  const [mode, setMode] = useState('client');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ clientId: '', directAccountType: '', directAccountId: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ date: new Date().toISOString().slice(0,10), amountUsd: null, description: '', reference: '', feeUsd: null });
  const [addExplain, setAddExplain] = useState({ enabled: false, mode: 'client', clientId: '', directAccountType: '', directAccountId: '' });
  const [adding, setAdding] = useState(false);

  // CSV import functionality removed per requirements

  const fetchEntities = async () => {
    const snap = await getDocs(collection(db, 'entities'));
    // Active Direct Entities only
    setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => ['vendor','partner','company'].includes(e.entityType) && e.status !== 'inactive'));
  };
  const fetchClients = async () => {
    const snap = await getDocs(collection(db, 'clients'));
    // Keep all clients so explained lines can still resolve names; dropdowns filter to active only
    setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };
  const fetchLines = async () => {
    setLoading(true);
    const qy = query(
      collection(db, 'bank_lines'),
      orderBy('date', 'desc'),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(qy);
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const getTimeMs = (v) => {
      if (!v) return 0;
      if (typeof v === 'number') return v;
      if (v instanceof Date) return v.getTime();
      if (v && typeof v.toDate === 'function') return v.toDate().getTime();
      if (v && typeof v.toMillis === 'function') return v.toMillis();
      const parsed = Date.parse(v);
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    rows.sort((a, b) => {
      const dCmp = String(b.date || '').localeCompare(String(a.date || ''));
      if (dCmp !== 0) return dCmp; // primary: date desc (YYYY-MM-DD lexical)
      const tB = getTimeMs(b.createdAt);
      const tA = getTimeMs(a.createdAt);
      if (tB !== tA) return tB - tA; // secondary: createdAt desc
      return String(b.id).localeCompare(String(a.id)); // final deterministic tiebreaker
    });
    setLines(rows);
    setLoading(false);
  };

  useEffect(() => { fetchEntities(); fetchClients(); fetchLines(); }, []);

  useEffect(() => {
    if (tableRef.current) tableRef.current.scrollTop = 0;
  }, [currentPage, rowsPerPage]);

  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n||0));

  const totalBalance = useMemo(() => {
    return lines.reduce((sum, l) => sum + Number(l.amountUsd || 0), 0);
  }, [lines]);

  const explain = async () => {
    if (!selected) return;
    try {
      if (mode === 'client') {
        const result = await explainAsClient({
          bankLineId: selected.id,
          clientId: form.clientId,
          amountUsd: Number(selected.amountUsd || 0),
          description: selected.description || 'Client balance from bank',
          memo: { text: selected.description || 'Client balance from bank' },
          explainedBy: currentUser?.uid || userProfile?.uid,
        });
        if (result?.availableBalance !== null && Number.isFinite(Number(result?.availableBalance))) {
          setClients((previous) => previous.map((client) => (
            client.id === form.clientId
              ? { ...client, availableBalance: Number(result.availableBalance) }
              : client
          )));
        }
        setSnackbar({
          open: true,
          message: result?.availableBalance !== null && Number.isFinite(Number(result?.availableBalance))
            ? `Client credited. Available balance: ${formatCurrency(result.availableBalance)}.`
            : 'Client credited successfully.',
          severity: 'success',
        });
      } else {
        await explainAsEntity({ bankLineId: selected.id, directAccountType: form.directAccountType, directAccountId: form.directAccountId, amountUsd: selected.amountUsd, description: selected.description, explainedBy: currentUser?.uid || userProfile?.uid });
        setSnackbar({ open: true, message: 'Bank line explained successfully.', severity: 'success' });
      }
      setOpenExplain(false);
      setForm({ clientId: '', directAccountType: '', directAccountId: '' });
      await Promise.all([fetchLines(), fetchClients()]);
    } catch (error) {
      setSnackbar({ open: true, message: error?.message || 'Failed to explain bank line', severity: 'error' });
    }
  };

  // Client statuses are capitalized ('Active' | 'Inactive' | 'Suspended'); missing status defaults to Active
  const isActiveClientOption = (c) => Boolean(c) && String(c.status || 'Active').toLowerCase() === 'active';
  const isActiveDirectEntity = (e) => e && ['vendor','partner','company'].includes(e.entityType) && e.status !== 'inactive';

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BankIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">Bank Management</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isSuperAdmin() && (
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setOpenAddDialog(true)}>Add Transaction</Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" color="text.secondary">Total Balance</Typography>
          <Typography variant="h5" fontWeight="bold" color={Number(totalBalance) >= 0 ? 'success.main' : 'error.main'}>
            {formatCurrency(totalBalance)}
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer ref={tableRef} sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>State</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              ) : lines.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center">No bank lines</TableCell></TableRow>
              ) : lines.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage).map((l) => (
                <TableRow key={l.id} hover selected={selected?.id === l.id} onClick={() => setSelected(l)}>
                  <TableCell>{l.date}</TableCell>
                  <TableCell>{l.description}</TableCell>
                  <TableCell align="right"><Typography color={Number(l.amountUsd) >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">{Number(l.amountUsd) >= 0 ? '+' : ''}{formatCurrency(l.amountUsd)}</Typography></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={l.state || 'unexplained'} color={l.state === 'client' ? 'success' : l.state === 'entity' ? 'warning' : 'default'} />
                      {l.state === 'unexplained' && (
                        <Button size="small" variant="text" onClick={(e)=>{ e.stopPropagation(); setSelected(l); setMode('client'); setForm({ clientId: '', directAccountType: '', directAccountId: '' }); setOpenExplain(true); }}>Explain</Button>
                      )}
                      {l.state === 'client' && (
                        <Typography variant="caption" color="text.secondary">
                          Client: {(clients.find(c=>c.id===l.clientId)?.clientName) || (clients.find(c=>c.id===l.clientId)?.name) || l.clientId}
                        </Typography>
                      )}
                      {l.state === 'entity' && (
                        <Typography variant="caption" color="text.secondary">
                          Entity: {l.directAccountType} - {entities.find(e=>e.id===l.directAccountId)?.entityName || l.directAccountId}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={lines.length} page={currentPage} rowsPerPage={rowsPerPage} sx={{ flexShrink: 0 }}
          onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} />
      </Paper>

      <Dialog open={openExplain} onClose={() => setOpenExplain(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Explain Bank Line</DialogTitle>
        <DialogContent>
          <TextField select fullWidth size="small" sx={{ mt: 1, mb: 2 }} label="Explanation" value={mode} onChange={(e)=>setMode(e.target.value)}>
            <MenuItem value="client">Client (C)</MenuItem>
            <MenuItem value="entity">Entity (E)</MenuItem>
          </TextField>
          {mode === 'client' ? (
            <Autocomplete options={clients.filter(isActiveClientOption)} getOptionLabel={(c)=>c.clientName || c.name || ''} value={clients.find(c=>c.id===form.clientId)||null} onChange={(e,v)=>setForm({ ...form, clientId: v ? v.id : '' })} renderInput={(p)=>(<TextField {...p} label="Client" placeholder="Select client" />)} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField select fullWidth size="small" label="Direct Account Type" value={form.directAccountType} onChange={(e)=>setForm({ ...form, directAccountType: e.target.value, directAccountId: '' })}>
                  <MenuItem value="vendor">Vendor</MenuItem>
                  <MenuItem value="partner">Partner</MenuItem>
                  <MenuItem value="company">Company</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete options={entities.filter(e=>e.entityType===form.directAccountType).filter(isActiveDirectEntity)} getOptionLabel={(e)=>e.entityName} value={entities.find(e=>e.id===form.directAccountId)||null} onChange={(e,v)=>setForm({ ...form, directAccountId: v ? v.id : '' })} renderInput={(p)=>(<TextField {...p} label="Direct Entity" placeholder="Select" />)} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpenExplain(false)}>Cancel</Button>
          <Button variant="contained" onClick={explain} disabled={!selected || (mode==='client' ? !form.clientId : (!form.directAccountType || !form.directAccountId))}>Explain</Button>
        </DialogActions>
      </Dialog>

      {/* Add Transaction Dialog */}
      <Dialog open={openAddDialog && isSuperAdmin()} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            New Bank Transaction
            <Typography variant="subtitle2" fontWeight="bold" color={Number((addForm.amountUsd||0) + (addForm.feeUsd||0)) >= 0 ? 'success.main' : 'error.main'}>
              Total: {Number((addForm.amountUsd||0) + (addForm.feeUsd||0)) >= 0 ? '+' : ''}{formatCurrency(Number((addForm.amountUsd||0) + (addForm.feeUsd||0)))}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Date" type="date" value={addForm.date} onChange={(e)=>setAddForm({ ...addForm, date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumericField fullWidth label="Amount (signed)" value={addForm.amountUsd} onValueChange={(n)=>setAddForm({ ...addForm, amountUsd: n })} mode="decimal" allowNegative decimals={2} />
            </Grid>
           
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Reference" value={addForm.reference} onChange={(e)=>setAddForm({ ...addForm, reference: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumericField fullWidth label="Fee (signed)" value={addForm.feeUsd} onValueChange={(n)=>setAddForm({ ...addForm, feeUsd: n })} mode="decimal" allowNegative decimals={2} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={addForm.description} onChange={(e)=>setAddForm({ ...addForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={addExplain.enabled} onChange={(e)=>setAddExplain({ ...addExplain, enabled: e.target.checked })} />} label="Explain now" />
            </Grid>
            {addExplain.enabled && (
              <>
                <Grid item xs={12}>
                  <TextField select fullWidth size="small" sx={{ mt: 1 }} label="Explanation" value={addExplain.mode} onChange={(e)=>setAddExplain({ ...addExplain, mode: e.target.value })}>
                    <MenuItem value="client">Client (C)</MenuItem>
                    <MenuItem value="entity">Entity (E)</MenuItem>
                  </TextField>
                </Grid>
                {addExplain.mode === 'client' ? (
                  <Grid item xs={12}>
                    <Autocomplete options={clients.filter(isActiveClientOption)} getOptionLabel={(c)=>c.clientName || c.name || ''} value={clients.find(c=>c.id===addExplain.clientId)||null} onChange={(e,v)=>setAddExplain({ ...addExplain, clientId: v ? v.id : '' })} renderInput={(p)=>(<TextField {...p} label="Client" placeholder="Select client" />)} />
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField select fullWidth size="small" label="Direct Account Type" value={addExplain.directAccountType} onChange={(e)=>setAddExplain({ ...addExplain, directAccountType: e.target.value, directAccountId: '' })}>
                        <MenuItem value="vendor">Vendor</MenuItem>
                        <MenuItem value="partner">Partner</MenuItem>
                        <MenuItem value="company">Company</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete size="small" fullWidth options={entities.filter(e=>e.entityType===addExplain.directAccountType).filter(e=>e && ['vendor','partner','company'].includes(e.entityType) && e.status !== 'inactive')} getOptionLabel={(e)=>e.entityName} value={entities.find(e=>e.id===addExplain.directAccountId)||null} onChange={(e,v)=>setAddExplain({ ...addExplain, directAccountId: v ? v.id : '' })} renderInput={(p)=>(<TextField {...p} label="Direct Entity" placeholder="Select" size="small" />)} />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpenAddDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={adding || !isSuperAdmin() || !addForm.date || addForm.amountUsd == null || (addExplain.enabled && (addExplain.mode==='client' ? !addExplain.clientId : (!addExplain.directAccountType || !addExplain.directAccountId)))} onClick={async ()=>{
            try {
              setAdding(true);
              const creatorId = currentUser?.uid || userProfile?.uid;
              const mainLine = await createBankLine({ date: addForm.date, amountUsd: Number(addForm.amountUsd), description: addForm.description, reference: addForm.reference, createdBy: creatorId });

              // Identify a company entity for ledger postings
              const companyEntity = entities.find(e => e && e.entityType === 'company' && e.status !== 'inactive');

              if (addExplain.enabled && mainLine?.id) {
                if (addExplain.mode === 'client') {
                  // Explain main to client balance; do NOT create a ledger entry for main
                  const clientResult = await explainAsClient({
                    bankLineId: mainLine.id,
                    clientId: addExplain.clientId,
                    amountUsd: Number(addForm.amountUsd),
                    description: addForm.description || 'Client balance from bank',
                    memo: { text: addForm.description || 'Client balance from bank' },
                    explainedBy: creatorId,
                  });
                  if (clientResult?.availableBalance !== null && Number.isFinite(Number(clientResult?.availableBalance))) {
                    setClients((previous) => previous.map((client) => (
                      client.id === addExplain.clientId
                        ? { ...client, availableBalance: Number(clientResult.availableBalance) }
                        : client
                    )));
                  }
                  // Only when explained: optionally create and explain fee to company
                  const hasFee = addForm.feeUsd != null && Number(addForm.feeUsd) !== 0;
                  if (hasFee && companyEntity) {
                    const feeDescription = addForm.description ? `${addForm.description} (Fee)` : 'Bank fee';
                    const feeAmount = Number(addForm.feeUsd);
                    const feeLine = await createBankLine({ date: addForm.date, amountUsd: feeAmount, description: feeDescription, reference: addForm.reference, createdBy: creatorId });
                    await explainAsEntity({ bankLineId: feeLine.id, directAccountType: 'company', directAccountId: companyEntity.id, amountUsd: feeAmount, description: feeDescription, explainedBy: creatorId });
                  }
                } else {
                  // Explain main as selected entity (creates ledger)
                  await explainAsEntity({ bankLineId: mainLine.id, directAccountType: addExplain.directAccountType, directAccountId: addExplain.directAccountId, amountUsd: Number(addForm.amountUsd), description: addForm.description, explainedBy: creatorId });
                  // Only when explained: optionally create and explain fee to company
                  const hasFee = addForm.feeUsd != null && Number(addForm.feeUsd) !== 0;
                  if (hasFee && companyEntity) {
                    const feeDescription = addForm.description ? `${addForm.description} (Fee)` : 'Bank fee';
                    const feeAmount = Number(addForm.feeUsd);
                    const feeLine = await createBankLine({ date: addForm.date, amountUsd: feeAmount, description: feeDescription, reference: addForm.reference, createdBy: creatorId });
                    await explainAsEntity({ bankLineId: feeLine.id, directAccountType: 'company', directAccountId: companyEntity.id, amountUsd: feeAmount, description: feeDescription, explainedBy: creatorId });
                  }
                }
              } else {
                // No explain now: do not post to ledger
              }
              setOpenAddDialog(false);
              setAddForm({ date: new Date().toISOString().slice(0,10), amountUsd: null, description: '', reference: '', feeUsd: null });
              setAddExplain({ enabled: false, mode: 'client', clientId: '', directAccountType: '', directAccountId: '' });
              await Promise.all([fetchLines(), fetchClients()]);
              setSnackbar({ open: true, message: 'Bank transaction added successfully.', severity: 'success' });
            } catch (e) { setSnackbar({ open: true, message: e?.message || 'Failed to create transaction', severity: 'error' }); }
            finally { setAdding(false); }
          }}>{adding ? <CircularProgress size={18} color="inherit" /> : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Import CSV functionality removed per requirements */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
